import React from "react";
import { PPTTiles } from "./PPTTiles";
import { API_URL } from "../config";
import { Button } from "./ui";
import { ChevronLeft, ChevronRight, Maximise, Play, Slides } from "./icons";

/**
 * The slide stage.
 *
 * A deck is read full screen — that's where page tracking and the real reading
 * happens — so this surface's only job is to present the deck and get out of
 * the way. It shows a blurred first-slide poster (the deck itself is never
 * exposed here), one unmistakable way in, and lesson navigation underneath.
 *
 * Previously this was a 100vw × 100vh block with a decorative play PNG and two
 * white patches taped over the embed's chrome; it now sits inside the section
 * stage like every other mode.
 */
export const PPTViewer = ({
  ppt,
  items,
  selectNext,
  selectPrevious,
  onItemClick,
  handleFullScreen,
  selectedPPTIndex,
}) => {
  const total = items?.items?.length || 0;
  const position = (selectedPPTIndex ?? 0) + 1;

  return (
    <div>
      {ppt.ppt ? (
        <div
          style={{
            position: "relative",
            margin: "var(--s-5)",
            borderRadius: "var(--r-lg)",
            overflow: "hidden",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border)",
            minHeight: 380,
            display: "grid",
            placeItems: "center",
          }}
        >
          {ppt.thumbnail && (
            <img
              src={`${API_URL}/api/ppt/thumbnail/${ppt.thumbnail}`}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(6px)",
                opacity: 0.5,
              }}
            />
          )}

          <button
            type="button"
            onClick={handleFullScreen}
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--s-3)",
              padding: "var(--s-6) var(--s-7)",
              background: "rgba(255,255,255,.9)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-lg)",
              backdropFilter: "blur(2px)",
              cursor: "pointer",
              font: "inherit",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <span
              className="ds-tile ds-tile--brand ds-tile--lg"
              style={{ width: 52, height: 52 }}
            >
              <Play size={22} />
            </span>
            <span className="ds-h3">{ppt.title}</span>
            <span className="ds-sub" style={{ margin: 0 }}>
              Opens full screen · press Esc to exit
            </span>
          </button>
        </div>
      ) : (
        <div className="ds-empty" style={{ minHeight: 260 }}>
          <span className="ds-empty__icon">
            <Slides size={20} />
          </span>
          <p className="ds-empty__text">
            The presentation for this lesson hasn't been published yet.
          </p>
        </div>
      )}

      {/* Lesson navigation — the deck's own paging happens full screen. */}
      {total > 1 && (
        <div
          className="ds-row-flex ds-between"
          style={{
            padding: "var(--s-3) var(--s-5)",
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          <Button
            variant="secondary"
            size="sm"
            icon={ChevronLeft}
            disabled={selectedPPTIndex === 0}
            onClick={() => selectPrevious(selectedPPTIndex)}
          >
            Previous
          </Button>
          <span className="ds-sub ds-num" style={{ margin: 0 }}>
            Lesson {position} of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            iconRight={ChevronRight}
            onClick={() => selectNext(selectedPPTIndex)}
          >
            Next
          </Button>
        </div>
      )}

      {ppt.ppt && (
        <>
          <div
            className="ds-row-flex ds-center"
            style={{ padding: "0 var(--s-5) var(--s-5)" }}
          >
            <Button icon={Maximise} onClick={handleFullScreen}>
              Open presentation
            </Button>
          </div>
          <PPTTiles
            pptTiles={items}
            onItemClick={onItemClick}
            selectedItem={selectedPPTIndex}
          />
        </>
      )}
    </div>
  );
};

export default PPTViewer;
